/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread
} from 'lexical';
import * as React from 'react';
import { Suspense } from 'react';

export type Options = ReadonlyArray<Option>;

export type Option = Readonly<{
  text: string;
  uid: string;
  votes: Array<number>;
  isVoted: boolean;
}>;

const PollComponent = React.lazy(() => import('./PollComponent'));

function createUID(): string {
  return Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substr(0, 5);
}

export function createPollOption(text = ''): Option {
  return {
    text,
    uid: createUID(),
    votes: [],
    isVoted: false
  };
}

function cloneOption(option: Option, text: string, votes?: Array<number>): Option {
  return {
    text,
    uid: option.uid,
    votes: votes || Array.from(option.votes),
    isVoted: option.isVoted
  };
}

export type SerializedPollNode = Spread<
  {
    id: string;
    question: string;
    options: Options;
    createdAt: Date;
    userId: string;
    durationDays: number;
    durationHours: number;
    durationMinutes: number;
    allowMultipleChoices: boolean;
  },
  SerializedLexicalNode
>;

function $convertPollElement(domNode: HTMLElement): DOMConversionOutput | null {
  const id = domNode.getAttribute('data-lexical-poll-id');
  const question = domNode.getAttribute('data-lexical-poll-question');
  const options = domNode.getAttribute('data-lexical-poll-options');
  const createdAt = new Date(domNode.getAttribute('data-lexical-created-at'));
  const userId = domNode.getAttribute('data-lexical-user-id');
  const durationDays = Number(domNode.getAttribute('data-lexical-poll-duration-days')) || 0;
  const durationHours = Number(domNode.getAttribute('data-lexical-poll-duration-hours')) || 0;
  const durationMinutes = Number(domNode.getAttribute('data-lexical-poll-duration-minutes')) || 0;
  const allowMultipleChoices = Boolean(
    domNode.getAttribute('data-lexical-poll-allow-multiple-choices')
  );

  if (question !== null && options !== null) {
    const node = $createPollNode(
      id,
      question,
      JSON.parse(options),
      createdAt,
      userId,
      durationDays,
      durationHours,
      durationMinutes,
      allowMultipleChoices
    );
    return { node };
  }
  return null;
}

export class PollNode extends DecoratorNode<JSX.Element> {
  __id: string;
  __question: string;
  __options: Options;
  __pollDuration_days: number;
  __pollDuration_hours: number;
  __pollDuration_minutes: number;
  __allowMultipleChoices: boolean;
  __createdAt: Date;
  __userId: string;

  static getType(): string {
    return 'poll';
  }

  static clone(node: PollNode): PollNode {
    return new PollNode(
      node.__id,
      node.__question,
      node.__options,
      node.__createdAt,
      node.__userId,
      node.__pollDuration_days,
      node.__pollDuration_hours,
      node.__pollDuration_minutes,
      node.__allowMultipleChoices,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedPollNode): PollNode {
    const question = serializedNode.question;
    const options = serializedNode.options;
    const createdAt = serializedNode.createdAt || new Date();
    const userId = serializedNode.userId || 'unknown';

    const node = $createPollNode(
      serializedNode.id,
      question,
      options,
      createdAt,
      userId,
      serializedNode.durationDays || 0,
      serializedNode.durationHours || 0,
      serializedNode.durationMinutes || 0,
      serializedNode.allowMultipleChoices || false
    );
    return node;
  }

  static validatePollData(data: { questions: string; options: Array<{ text: string }> }): boolean {
    if (data?.questions === '' || data?.options?.some((option) => option.text === '')) return false;

    return true;
  }

  constructor(
    id: string,
    question: string,
    options: Options,
    createdAt: Date,
    userId: string,
    durationDays: number,
    durationHours: number,
    durationMinutes: number,
    allowMultipleChoices: boolean,
    key?: NodeKey
  ) {
    super(key);
    this.__id = id;
    this.__question = question;
    this.__options = options;
    this.__createdAt = createdAt;
    this.__userId = userId;
    this.__pollDuration_days = durationDays;
    this.__pollDuration_hours = durationHours;
    this.__pollDuration_minutes = durationMinutes;
    this.__allowMultipleChoices = allowMultipleChoices;
  }

  exportJSON(): SerializedPollNode {
    return {
      id: this.__id,
      options: this.__options,
      question: this.__question,
      createdAt: this.__createdAt,
      userId: this.__userId,
      durationDays: this.__pollDuration_days,
      durationHours: this.__pollDuration_hours,
      durationMinutes: this.__pollDuration_minutes,
      allowMultipleChoices: this.__allowMultipleChoices,
      type: 'poll',
      version: 1
    };
  }

  setPollDurationDays(value: number): void {
    const self = this.getWritable();
    self.__pollDuration_days = value;
  }

  setPollDurationHours(value: number): void {
    const self = this.getWritable();
    self.__pollDuration_hours = value;
  }

  setPollDurationMinutes(value: number): void {
    const self = this.getWritable();
    self.__pollDuration_minutes = value;
  }

  setAllowMultipleChoices(value: boolean): void {
    const self = this.getWritable();
    self.__allowMultipleChoices = value;
  }

  setQuestion(question: string): void {
    const self = this.getWritable();
    self.__question = question;
  }

  addOption(option: Option): any {
    const self = this.getWritable();
    const options = Array.from(self.__options);
    options.push(option);
    self.__options = options;
    return options;
  }

  deleteOption(option: Option): any {
    const self = this.getWritable();
    const options = Array.from(self.__options);
    const index = options.indexOf(option);
    options.splice(index, 1);
    self.__options = options;

    return options;
  }

  setOptionText(option: Option, text: string): void {
    const self = this.getWritable();
    self.__options = self.__options.map((__option) => {
      if (__option.uid === option.uid)
        return {
          ...__option,
          text
        };
      else return __option;
    });
  }

  setOptions(options: Option[]): void {
    const self = this.getWritable();
    self.__options = options;
  }

  toggleVote(option: Option, isAuthor: boolean, isMultipleChoices: boolean): any {
    const self = this.getWritable();
    self.__options = self.__options.map((__option) => {
      if (__option.uid === option.uid)
        return {
          ...__option,
          isVoted: !__option.isVoted
        };
      else {
        let otherVotesValue = isMultipleChoices ? __option.isVoted : false;
        return {
          ...__option,
          isVoted: otherVotesValue
        };
      }
    });

    return self.__options;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-poll-question')) {
          return null;
        }
        return {
          conversion: $convertPollElement,
          priority: 2
        };
      }
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-poll-id', this.__id);
    element.setAttribute('data-lexical-poll-question', this.__question);
    element.setAttribute('data-lexical-poll-options', JSON.stringify(this.__options));
    element.setAttribute('data-lexical-created-at', String(this.__createdAt));
    element.setAttribute('data-lexical-user-id', String(this.__userId));
    element.setAttribute('data-lexical-poll-duration-days', String(this.__pollDuration_days));
    element.setAttribute('data-lexical-poll-duration-hours', String(this.__pollDuration_hours));
    element.setAttribute('data-lexical-poll-duration-minutes', String(this.__pollDuration_minutes));
    element.setAttribute(
      'data-lexical-poll-allow-multiple-choices',
      String(this.__allowMultipleChoices)
    );
    return { element };
  }

  createDOM(): HTMLElement {
    const elem = document.createElement('span');
    elem.style.display = 'inline-block';
    return elem;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={null}>
        <PollComponent
          id={this.__id}
          question={this.__question}
          initialOptions={this.__options}
          createdAt={this.__createdAt || new Date()}
          authorId={this.__userId || 'unknown'}
          durationDays={this.__pollDuration_days || 0}
          durationHours={this.__pollDuration_hours || 0}
          durationMinutes={this.__pollDuration_minutes || 0}
          allowMultipleChoices={this.__allowMultipleChoices || false}
          nodeKey={this.__key}
        />
      </Suspense>
    );
  }
}

export function $createPollNode(
  id: string,
  question: string,
  options: Options,
  createdAt: Date,
  userId: string,
  durationDays: number = 0,
  durationHours: number = 0,
  durationMinutes: number = 0,
  allowMultipleChoices: boolean = false
): PollNode {
  return new PollNode(
    id,
    question,
    options,
    createdAt,
    userId,
    durationDays,
    durationHours,
    durationMinutes,
    allowMultipleChoices
  );
}

export function $isPollNode(node: LexicalNode | null | undefined): node is PollNode {
  return node instanceof PollNode;
}
